import { type MouseEventHandler, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import CircularProgress from '@mui/material/CircularProgress';
import type { OrderProductStatusId } from '@witmetrics/utils';
import { ORDER_PRODUCT_STATUS_COLORS } from '@witmetrics/unison-core';
import { useToggle } from '@/hooks/useToggle';
import OrderProductStatusCircle from '@/components/OrderProductStatusCircle';
import LoopingEllipsis from '@/components/LoopingEllipsis';
import OrderProductStatusMenu from '@/menus/OrderProductStatusMenu';
import Tooltip from '@/components/Tooltip';
import { LABELS } from './constants';
import { classes } from './utils';

export type OrderProductStatusProps = {
  showLabel?: boolean;
  isSaving?: boolean;
  className?: string;
  labelClassName?: string;
  orderProductStatusId: OrderProductStatusId;
  onChange: (statusId: OrderProductStatusId) => void;
};

export default function OrderProductStatus({
  showLabel = true,
  isSaving,
  className,
  labelClassName,
  orderProductStatusId,
  onChange,
}: OrderProductStatusProps) {
  const statusRef = useRef(null);
  const [isMenuOpen, toggleMenu] = useToggle();

  const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleMenu();
  };

  const handleChange = (statusId: OrderProductStatusId) => {
    toggleMenu(false);
    onChange(statusId);
  };

  const size = showLabel ? 14 : 20;

  return (
    <>
      <button
        ref={statusRef}
        className={twMerge(classes.button, className)}
        onClick={handleClick}>
        {isSaving ? (
          <>
            <CircularProgress size={size} color="grey-6" />
            {showLabel && (
              <span>
                Saving
                <LoopingEllipsis />
              </span>
            )}
          </>
        ) : (
          <>
            {showLabel ? (
              <OrderProductStatusCircle
                orderProductStatusId={orderProductStatusId}
                size={size}
              />
            ) : (
              <Tooltip
                placement="top"
                enterDelay={400}
                title={LABELS[orderProductStatusId]}>
                <OrderProductStatusCircle
                  orderProductStatusId={orderProductStatusId}
                  size={size}
                />
              </Tooltip>
            )}
            {showLabel && (
              <span
                className={twMerge(
                  classes.label,
                  ORDER_PRODUCT_STATUS_COLORS[orderProductStatusId].text,
                  labelClassName
                )}>
                {LABELS[orderProductStatusId]}
              </span>
            )}
          </>
        )}
      </button>
      {isMenuOpen && (
        <OrderProductStatusMenu
          anchorTo={statusRef.current}
          open
          onChange={handleChange}
          onClose={() => toggleMenu(false)}
        />
      )}
    </>
  );
}
